import type { ExternalTableSchema } from '@packages/table-builder';
import { rsuAwardBulkRowSelectable } from '@apps/customer/domains/rsu-awards/rsuAwardsBulkSelection';

export const rsuAwardsTable: ExternalTableSchema = {
  metadata: {
    title: 'RSU & RSA Grants Issued',
    entity: 'Restricted Stock Award',
  },

  dataSource: {
    endpoint: '/api/award/api/app/r-sUAward/r-sUAward-list',
    method: 'GET',
    rowIdField: 'id',
    responseMapping: {
      items: 'items',
      totalCount: 'totalCount',
    },
    staticQueryParams: {
      RSUStatus: 0,
      ApprovalStatusIds: [0],
      EmployeeStatuses: [],
    },
    requestParams: {
      Filter: 'table.searchText',
      Sorting: 'table.sorting',
      SkipCount: 'table.pageOffset',
      MaxResultCount: 'table.pageSize',
      ApprovalStatusIds: 'filters.approvalStatusIdOrDefault',
      RSUAcceptance: 'filters.optionAcceptance',
      CreatedFromDate: 'filters.createdFromDate',
      CreatedToDate: 'filters.createdToDate',
      GrantFromDate: 'filters.grantFromDate',
      GrantToDate: 'filters.grantToDate',
      AwardTypeFilter: 'filters.awardTypeFilter',
      ApprovedFromDate: 'filters.fromApproveDate',
      ApprovedToDate: 'filters.toApproveDate',
      PublishFromDate: 'filters.fromPublishDate',
      PublishToDate: 'filters.toPublishDate',
    },

    filters: [
      {
        name: 'CreatedFromDate',
        placeholder: 'Created Date From',
        type: 'date',
      },
      {
        name: 'CreatedToDate',
        placeholder: 'Created Date To',
        type: 'date',
      },
      {
        name: 'ApprovedFromDate',
        placeholder: 'Approve Date From',
        type: 'date',
      },
      {
        name: 'ApprovedToDate',
        placeholder: 'Approve Date To',
        type: 'date',
      },
      {
        name: 'PublishFromDate',
        placeholder: 'Publish Date From',
        type: 'date',
      },
      {
        name: 'PublishToDate',
        placeholder: 'Publish Date To',
        type: 'date',
      },
      {
        name: 'ApprovalStatusIds',
        placeholder: 'Approval Status',
        type: 'asyncSelect',
        multiple: true,
        dataSource: {
          url: '/api/award/api/app/r-sUAward/r-sUAward-statuses',
          labelField: 'name',
          valueField: 'id',
        },
      },
      {
        name: 'RSUAcceptance',
        placeholder: 'Accept Status',
        type: 'asyncSelect',
        dataSource: {
          url: '/api/award/api/app/r-sUAward/r-sUAcceptances',
          labelField: 'name',
          valueField: 'id',
        },
      },
      {
        name: 'GrantFromDate',
        placeholder: 'Grant Date From',
        type: 'date',
      },
      {
        name: 'GrantToDate',
        placeholder: 'Grant Date To',
        type: 'date',
      },
      {
        name: 'AwardTypeFilter',
        placeholder: 'Award Type',
        type: 'select',
        options: [
          { label: 'RSU', value: 'RSU' },
          { label: 'RSA', value: 'RSA' },
        ],
      },
    ],
  },

  columns: [
    {
      field: 'grantNo',
      header: 'Award #',
      sortable: true,
      width: 150,
      visibility: 'locked',
    },
    {
      field: 'participantName',
      header: 'Participant Details',
      type: 'couple',
      sortable: true,
      couple: { titleField: 'participantName', subtitleField: 'employeeId' },
      width: 150,
      visibility: 'locked',
    },
    {
      field: 'isUnvestedAcquisition',
      header: 'Restricted Units',
      type: 'badge',
      sortable: true,
      width: 180,
      align: 'center',
      conditionalValues: { true: 'RSA', false: 'RSU', null: 'RSU' },
      badge: {
        statusMap: { true: 'success', false: 'success', null: 'success' },
        defaultVariant: 'success',
      },
    },
    {
      field: 'issueDate',
      header: 'Grant Date',
      type: 'date',
      sortable: true,
      width: 150,
      align: 'center',
    },
    {
      field: 'grantName',
      header: 'Grant Name',
      sortable: true,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'planName',
      header: 'Plan Name',
      width: 150,
      visibility: 'picker',
    },
    {
      field: 'status',
      header: 'Approval Status',
      type: 'badge',
      sortable: true,
      width: 180,
      align: 'center',
      uppercase: true,
      badge: {
        statusMap: {
          Approved: 'success',
          Cancelled: 'error',
          Rejected: 'error',
          Pending: 'info',
        },
        defaultVariant: 'info',
      },
    },
    {
      field: 'unitsGranted',
      header: 'Awarded Units',
      type: 'number',
      sortable: true,
      width: 150,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'vestedUnits',
      header: 'Vested Units',
      type: 'number',
      width: 180,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'releasedUnits',
      header: 'Released Units',
      type: 'number',
      sortable: false,
      width: 180,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'cancelledUnits',
      header: 'Canceled Units',
      type: 'number',
      sortable: false,
      width: 180,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'outStandingValue',
      header: 'Outstanding Value',
      type: 'currency',
      sortable: true,
      width: 180,
      align: 'center',
      visibility: 'picker',
      format: { decimals: 2 },
    },
    {
      field: 'unvestedUnit',
      header: 'Unvested Units',
      type: 'number',
      width: 180,
      align: 'center',
    },
    {
      field: 'outStandingUnits',
      header: 'Outstanding Units',
      type: 'number',
      sortable: false,
      width: 180,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'isAwardPublished',
      header: 'Publish Status',
      type: 'badge',
      width: 180,
      align: 'center',
      conditionalValues: { true: 'PUBLISHED', false: 'NOT PUBLISHED' },
      badge: {
        statusMap: { true: 'success', false: 'info' },
        defaultVariant: 'info',
      },
    },
    {
      field: 'vestingSchedule',
      header: 'Vesting Schedule',
      sortable: true,
      width: 180,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'nextVestingDate',
      header: 'Next Vesting Date',
      type: 'date',
      width: 180,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'isAccepted',
      header: 'Accept Status',
      type: 'badge',
      sortable: true,
      width: 180,
      align: 'center',
      visibility: 'picker',
      conditionalValues: { true: 'ACCEPTED', false: 'NEEDS ACCEPTANCE' },
      badge: {
        statusMap: { true: 'success', false: 'error' },
        defaultVariant: 'error',
      },
    },
    {
      field: 'acceptanceMethod',
      header: 'Acceptance Method',
      type: 'badge',
      width: 180,
      align: 'center',
      conditionalValues: { '1': 'ONLINE', '2': 'OFFLINE' },
      badge: {
        statusMap: { '1': 'success', '2': 'error' },
        defaultVariant: 'info',
      },
    },
  ],

  actions: {
    row: [
      { id: 'SUMMARY_RSU_AWARDS', label: 'Summary' },
      { id: 'VIEW_RSU_AWARDS', label: 'View' },
      { id: 'EDIT_RSU_AWARDS', label: 'Edit' },
      { id: 'DELETE_RSU_AWARDS', label: 'Delete', variant: 'danger' },
      { id: 'CANCEL_RSU_AWARDS', label: 'Cancel' },
      { id: 'PUBLISH_RSU_AWARDS', label: 'Publish Award' },
      { id: 'VIEW_COMMENTS_RSU_AWARDS', label: 'View Comments' },
      { id: 'VIEW_ACCEPTANCE_RSU_AWARDS', label: 'View Acceptance' },
    ],
    bulk: [
      { id: 'BULK_PUBLISH_RSU_AWARDS', label: 'Publish Award' },
      { id: 'BULK_DELETE_RSU_AWARDS', label: 'Bulk Delete', variant: 'danger' },
      { id: 'BULK_EXPORT_RSU_AWARDS', label: 'Bulk Export' },
    ],
    toolbar: {
      primary: {
        id: 'ADD_RSU_AWARDS',
        label: 'Grant New Restricted Stock',
        icon: 'add_circle_outline',
      },
      secondary: [
        {
          label: 'Downloads',
          icon: 'download',
          type: 'download',
          children: [
            { id: 'DOWNLOAD_TEMPLATE_RSU_AWARDS', label: 'Download Template' },
          ],
        },
        {
          label: 'Import',
          icon: 'upload',
          type: 'upload',
          children: [
            { id: 'IMPORT_RSU_AWARDS', label: 'Import Bulk XLS' },
          ],
        },
      ],
    },
  },

  features: {
    search: { enabled: true },
    pagination: { mode: 'infiniteScroll', defaultSize: 50 },
    selection: { keyField: 'id', selectable: rsuAwardBulkRowSelectable, selectAllPages: true },
    columnPreferences: { persistenceKey: 'RSU Admin', resizable: true, reorderable: true },
  },
};
